import { ArrowDownTrayIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import { type HasuraClaims, canDownloadCallRecordings } from 'utils/roles';

type RecordingAccess = {
  sid: string;
  duration?: number | null;
  status?: string | null;
  playbackUrl: string;
  downloadUrl: string | null;
};

type Props = {
  callId: string;
  token?: string;
  claims?: HasuraClaims;
  /** Optional Twilio CallSid to help locate recordings when payload has none. */
  callSid?: string | null;
  /** Optional known recording SIDs (from payload or call-details). */
  recordingSids?: string[];
};

export default function CallRecordingPlayer({
  callId,
  token,
  claims = {},
  callSid = null,
  recordingSids,
}: Props) {
  const [recordings, setRecordings] = useState<RecordingAccess[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const canDownload = canDownloadCallRecordings(claims);
  const recordingSidKey = useMemo(
    () => (recordingSids?.length ? recordingSids.join('|') : ''),
    [recordingSids],
  );

  useEffect(() => {
    if (!token || !callId) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/calls/recording', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            callId,
            callSid: callSid || undefined,
            recordingSid:
              recordingSids?.length === 1 ? recordingSids[0] : undefined,
          }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            body?.error?.message || 'Could not load call recordings.',
          );
        }

        let next = Array.isArray(body.recordings)
          ? (body.recordings as RecordingAccess[])
          : [];

        if (recordingSids?.length) {
          const allowed = new Set(recordingSids);
          next = next.filter((item) => allowed.has(item.sid));
        }

        if (!cancelled) {
          setRecordings(next);
        }
      } catch (err) {
        if (!cancelled) {
          setRecordings([]);
          setError(
            err instanceof Error ? err.message : 'Could not load recordings.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, callId, callSid, recordingSidKey]);

  if (!token) {
    return null;
  }

  if (loading && !recordings.length) {
    return (
      <p className="text-sm font-medium text-gray-500">Loading recording…</p>
    );
  }

  if (error && !recordings.length) {
    return <p className="text-sm font-medium text-red-600">{error}</p>;
  }

  if (!recordings.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      {recordings.map((recording) => (
        <div className="flex flex-wrap items-center gap-2" key={recording.sid}>
          <MicrophoneIcon
            className="h-4 w-4 flex-shrink-0 text-purple-600"
            aria-hidden="true"
          />
          <audio
            controls
            preload="metadata"
            src={recording.playbackUrl}
            controlsList={canDownload ? undefined : 'nodownload'}
            onContextMenu={
              canDownload
                ? undefined
                : (event) => {
                    event.preventDefault();
                  }
            }
            className="max-w-full"
          />
          {canDownload && recording.downloadUrl ? (
            <a
              href={recording.downloadUrl}
              className="inline-flex items-center gap-1 text-sm font-medium text-purple-700 hover:text-purple-900"
              download={`call-recording-${recording.sid}.mp3`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              Download
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
}
